import { CartLinesUpdateEvent } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const cardSelector = '[data-honho-quick-order]';
const states = new Map();
const debounceDelay = 280;
let cartQueue = Promise.resolve();

function number(value) {
  const parsed = Number.parseInt(String(value ?? '0'), 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function quantityFor(cart, variantId) {
  return (cart.items || []).reduce((quantity, item) => {
    return String(item.variant_id) === String(variantId) ? quantity + item.quantity : quantity;
  }, 0);
}

function stateFor(variantId) {
  const key = String(variantId);
  if (states.has(key)) return states.get(key);

  let initial = 0;
  document.querySelectorAll(`[data-honho-variant][data-variant-id="${CSS.escape(key)}"]`).forEach((variant) => {
    initial = Math.max(initial, number(variant.dataset.variantQuantity));
  });

  const state = {
    variantId: key,
    committed: initial,
    desired: initial,
    timer: null,
    enqueued: false,
    inFlight: false,
  };
  states.set(key, state);
  return state;
}

function setStatus(card, message = '', isError = false) {
  const status = card?.querySelector('[data-honho-status]');
  if (!status) return;
  status.classList.toggle('is-error', isError);
  status.textContent = message;
}

function updateCard(card, quantity, available = true) {
  const output = card.querySelector('[data-honho-quantity]');
  const minus = card.querySelector('[data-honho-minus]');
  const plus = card.querySelector('[data-honho-plus]');

  if (output) output.value = String(quantity);
  if (minus) minus.disabled = quantity < 1;
  if (plus) plus.disabled = !available;

  const selected = card.querySelector('[data-honho-variant].is-selected');
  if (selected) selected.dataset.variantQuantity = String(quantity);
}

function renderVariantQuantity(variantId, quantity) {
  document.querySelectorAll(cardSelector).forEach((card) => {
    card.querySelectorAll('[data-honho-variant]').forEach((variant) => {
      if (String(variant.dataset.variantId) === String(variantId)) {
        variant.dataset.variantQuantity = String(quantity);
      }
    });

    if (String(card.dataset.variantId) !== String(variantId)) return;
    const selected = card.querySelector('[data-honho-variant].is-selected');
    updateCard(card, quantity, selected?.dataset.variantAvailable !== 'false');
  });
}

function syncAllCards(cart, submittedVariantId = '', submittedTarget = null) {
  document.querySelectorAll('[data-honho-variant]').forEach((variant) => {
    const variantId = String(variant.dataset.variantId || '');
    if (!variantId) return;

    const actual = quantityFor(cart, variantId);
    const state = stateFor(variantId);
    const isSubmitted = variantId === String(submittedVariantId);
    const changedWhileWaiting = isSubmitted && state.desired !== submittedTarget;
    const hasPendingChange = changedWhileWaiting || (!isSubmitted && (state.timer || state.enqueued || state.inFlight));

    state.committed = actual;
    if (!hasPendingChange) state.desired = actual;
    variant.dataset.variantQuantity = String(hasPendingChange ? state.desired : actual);
  });

  document.querySelectorAll(cardSelector).forEach((card) => {
    const state = stateFor(card.dataset.variantId);
    const selected = card.querySelector('[data-honho-variant].is-selected');
    updateCard(card, state.desired, selected?.dataset.variantAvailable !== 'false');
  });

  document.querySelectorAll('cart-icon').forEach((icon) => {
    if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(cart.item_count, false);
  });

  document.dispatchEvent(new CustomEvent('honho:cart-updated', { detail: { cart } }));
}

function updateVariantPrice(card, variant) {
  const formattedPrice = variant.dataset.variantPrice;
  if (!formattedPrice) return;

  const productCard = card.closest('product-card');
  const container = productCard?.querySelector('product-price [ref="priceContainer"]');
  if (!container) return;

  let currentPrice = container.querySelector('.price');
  if (!currentPrice) {
    currentPrice = document.createElement('span');
    currentPrice.className = 'price';
    container.prepend(currentPrice);
  }
  currentPrice.textContent = formattedPrice;

  const comparePrice = variant.dataset.variantComparePrice || '';
  let compare = container.querySelector('.compare-at-price');

  if (comparePrice) {
    if (!compare) {
      compare = document.createElement('span');
      compare.className = 'price-item__group price-item--regular compare-at-price';
      currentPrice.after(compare);
    }
    compare.hidden = false;
    compare.textContent = comparePrice;
  } else if (compare) {
    compare.hidden = true;
    compare.textContent = '';
  }
}

function updatePreview(card, variant) {
  card.querySelectorAll('[data-honho-variant]').forEach((button) => {
    const selected = button === variant;
    button.classList.toggle('is-selected', selected);
    button.setAttribute('aria-pressed', String(selected));
  });

  card.dataset.variantId = variant.dataset.variantId;
  const label = card.querySelector('[data-honho-sku]');
  if (label) label.textContent = variant.dataset.variantSku || 'Available style';

  const state = stateFor(variant.dataset.variantId);
  updateCard(card, state.desired, variant.dataset.variantAvailable !== 'false');
  setStatus(card, variant.dataset.variantAvailable === 'false' ? 'Currently unavailable' : '');
  updateVariantPrice(card, variant);

  const productCard = card.closest('product-card');
  const mainImage = productCard?.querySelector(
    '.product-card-gallery img, .product-card__gallery img, slideshow-slide img, .product-card__content img'
  );

  if (mainImage && !card.contains(mainImage) && variant.dataset.variantImage) {
    mainImage.src = variant.dataset.variantImage;
    mainImage.removeAttribute('srcset');
  }
}

function friendlyError(result, response) {
  if (result && typeof result === 'object') {
    return result.description || result.message || result.errors || null;
  }
  if (response.status === 429) return 'Cart is busy. Please wait a moment and try again.';
  if (response.status >= 500) return 'Cart is temporarily unavailable. Please try again.';
  return null;
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .slice(0, 5);
}

function publishCartUpdate(cart, state) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'update',
      context: 'product',
      lines: [{ merchandiseId: state.variantId, quantity: state.committed }],
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      didError: false,
      itemCount: cart.item_count,
      items: cart.items,
      sections: cart.sections || {},
      source: 'honho-wholesale-quick-order',
    },
  });
}

async function updateCart(variantId, quantity, attempt = 0) {
  let response;

  try {
    const sectionIds = cartSectionIds();
    const payload = { updates: { [variantId]: quantity } };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    if (attempt < 2) {
      await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
      return updateCart(variantId, quantity, attempt + 1);
    }
    throw new Error('Network connection interrupted. Please try again.');
  }

  const text = await response.text();
  let result = null;

  if (text && !text.trimStart().startsWith('<')) {
    try {
      result = JSON.parse(text);
    } catch (_) {
      result = null;
    }
  }

  const retryable = response.status === 429 || response.status >= 500 || !result;
  if (retryable && attempt < 2) {
    await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
    return updateCart(variantId, quantity, attempt + 1);
  }

  if (!response.ok || !result || result.status >= 400 || result.errors) {
    throw new Error(friendlyError(result, response) || 'Unable to update your cart. Please try again.');
  }

  return result;
}

function markVariantCards(variantId, loading, message) {
  document.querySelectorAll(cardSelector).forEach((card) => {
    if (String(card.dataset.variantId) !== String(variantId)) return;
    card.classList.toggle('is-loading', loading);
    if (message !== undefined) setStatus(card, message);
  });
}

function enqueue(state) {
  if (state.enqueued || state.inFlight) return;
  state.enqueued = true;

  cartQueue = cartQueue
    .then(async () => {
      state.enqueued = false;
      state.inFlight = true;
      const submittedTarget = state.desired;
      markVariantCards(state.variantId, true, 'Updating cart…');

      try {
        const cart = await updateCart(state.variantId, submittedTarget);
        syncAllCards(cart, state.variantId, submittedTarget);
        publishCartUpdate(cart, state);
        const message = submittedTarget === 0 ? 'Removed from cart' : 'Cart updated';
        document.querySelectorAll(cardSelector).forEach((card) => {
          if (String(card.dataset.variantId) === state.variantId) setStatus(card, message);
        });
      } catch (error) {
        state.desired = state.committed;
        renderVariantQuantity(state.variantId, state.committed);
        document.querySelectorAll(cardSelector).forEach((card) => {
          if (String(card.dataset.variantId) === state.variantId) {
            setStatus(card, error instanceof Error ? error.message : 'Unable to update your cart. Please try again.', true);
          }
        });
      } finally {
        state.inFlight = false;
        markVariantCards(state.variantId, false);
        if (state.desired !== state.committed) enqueue(state);
      }
    })
    .catch(() => {
      state.enqueued = false;
      state.inFlight = false;
    });
}

function scheduleQuantity(card, delta) {
  const variantId = card.dataset.variantId;
  const selected = card.querySelector('[data-honho-variant].is-selected');
  if (!variantId || (delta > 0 && selected?.dataset.variantAvailable === 'false')) return;

  const state = stateFor(variantId);
  const next = Math.max(0, state.desired + delta);
  if (next === state.desired) return;

  state.desired = next;
  renderVariantQuantity(variantId, next);
  markVariantCards(variantId, false, 'Saving…');

  if (state.timer) clearTimeout(state.timer);
  state.timer = setTimeout(() => {
    state.timer = null;
    enqueue(state);
  }, debounceDelay);
}

function scheduleEnteredQuantity(input, commitEmpty = false) {
  const card = input.closest(cardSelector);
  if (!card || !card.dataset.variantId) return;

  const raw = input.value.trim();
  if (!raw && !commitEmpty) return;

  const state = stateFor(card.dataset.variantId);
  const next = Math.max(0, number(raw));
  const selected = card.querySelector('[data-honho-variant].is-selected');

  if (next > state.desired && selected?.dataset.variantAvailable === 'false') {
    input.value = String(state.desired);
    return;
  }

  if (input.value !== String(next)) input.value = String(next);
  scheduleQuantity(card, next - state.desired);
}

document.addEventListener('focusin', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-honho-quantity]')) return;
  input.select();
});

document.addEventListener('input', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-honho-quantity]')) return;
  scheduleEnteredQuantity(input);
});

document.addEventListener('change', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-honho-quantity]')) return;
  scheduleEnteredQuantity(input, true);
});

document.addEventListener('keydown', (event) => {
  const input = event.target instanceof HTMLInputElement ? event.target : null;
  if (!input?.matches('[data-honho-quantity]')) return;

  if (['e', 'E', '+', '-', '.'].includes(event.key)) {
    event.preventDefault();
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    scheduleEnteredQuantity(input, true);
    input.blur();
  }
});

document.addEventListener(
  'click',
  (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-honho-variant], [data-honho-minus], [data-honho-plus]');
    if (!control) return;

    const card = control.closest(cardSelector);
    if (!card) return;

    event.preventDefault();
    event.stopPropagation();

    if (control.matches('[data-honho-variant]')) {
      updatePreview(card, control);
    } else {
      scheduleQuantity(card, control.matches('[data-honho-plus]') ? 1 : -1);
    }
  },
  true
);

document.querySelectorAll(cardSelector).forEach((card) => {
  const selected = card.querySelector('[data-honho-variant].is-selected');
  if (selected) updateVariantPrice(card, selected);
});
